import { api } from "@vuumly-common/common";
import { ChartData, ChartOptions, TooltipItem } from "chart.js";
import Color from "color";
import { useQuery, useTheme, useTranslate } from "hooks";
import _ from "lodash";
import { useMemo } from "react";
import { CardProps } from "../..";
import { BaseBarChart } from "../../../Charts";
import { TextH5 } from "../../../Typography/Text";
import { BaseCard } from "../../Base.card";

export const RideDistributionOperatorTimeOfDayBarChart = (props: CardProps) => {
  const translate = useTranslate();
  const theme = useTheme();

  const { data, isLoading } = useQuery<ChartData<"bar", Record<any, number>[]>>(
    {
      apiEndpoint: api.Endpoint.RidesPerTimeOfDay,
    }
  );

  const clonedData = useMemo(() => {
    if (isLoading || !data?.datasets) {
      return;
    }
    const clonedData = _.cloneDeep(data);

    if (clonedData) {
      clonedData.labels = ["morning", "afternoon", "evening", "night"];
    }

    clonedData?.datasets.forEach((dataset) => {
      const { label } = dataset;

      if (label === "ride_percentage_operator") {
        dataset.label = translate("Your ride distribution");
        if (dataset.data.length) {
          dataset.backgroundColor = dataset.data.map(() => {
            return Color(theme.accentColor).string();
          });

          dataset.hoverBackgroundColor = dataset.data.map(() => {
            return Color(theme.accentColorHover).string();
          });
        }
        dataset.barPercentage = 0.99;
        dataset.parsing = {
          yAxisKey: "ride_percentage_operator",
        };
      } else if (label === "ride_percentage_without_operator") {
        dataset.label = translate("Ride distribution for others");
        if (dataset.data.length) {
          dataset.backgroundColor = dataset.data.map(() => {
            return Color(theme.otherOperatorBarColor).string();
          });

          dataset.hoverBackgroundColor = dataset.data.map(() => {
            return Color(theme.hoverOtherOperatorBarColor).string();
          });
        }
        dataset.barPercentage = 0.99;
        dataset.parsing = {
          yAxisKey: "ride_percentage_without_operator",
        };
      } else {
        dataset.hidden = true;
        dataset.label = "none";
      }
    });

    return clonedData;
  }, [data]);

  const options: ChartOptions<"bar"> = {
    parsing: {
      xAxisKey: "dimension",
    },

    scales: {
      x: {
        stacked: false,
        ticks: {
          callback: (idx) => {
            return translate(clonedData?.labels?.[idx as number] as string);
          },
        },
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: "Time of day",
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: "Ride distribution",
        },
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
    },

    plugins: {
      tooltip: {
        callbacks: {
          title: (ctx) => {
            return `${translate(ctx[0].label)} (${translate(
              ctx[0].label + "_range"
            )})`;
          },
          label: (item: TooltipItem<"bar">) => {
            return `${item.dataset.label}: ${item.formattedValue}%`;
          },
        },
      },
      datalabels: {
        display: true,
        formatter: (item: Record<string, any>) =>
          (item.ride_percentage_without_operator ||
            item.ride_percentage_operator) + "%",
      },
    },
  };

  return (
    <BaseCard
      isLoading={isLoading}
      header={<TextH5>{translate("Ride distribution by time of day")}</TextH5>}
    >
      <BaseBarChart options={options} data={clonedData} />
    </BaseCard>
  );
};
